import { useEffect } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuth } from '../../../context/AuthContext';
import { dictionary } from '../../../utils/dictionary';
import { Layers, CheckCircle2, Image } from 'lucide-react';

export function StepFunnelSummary() {
  const { language } = useAuth();
  const { multiFunnelData, estimatedCost, isFetchingEstimatedCost, fetchEstimatedCost } = useOnboardingStore();
  const { funnels } = multiFunnelData;

  const funnelColors = ['#6366F1', '#F97316', '#22C55E', '#EAB308', '#EC4899'];

  const totalSections = funnels.reduce(
    (acc, f) => acc + f.sections.filter(s => s.enabled).length,
    0
  );

  const totalAds = funnels.reduce(
    (acc, f) => acc + (f.selectedAdTemplates?.length || 0),
    0
  );

  useEffect(() => {
    fetchEstimatedCost();
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          {dictionary["all-ready-to-create"]?.[language] ?? "Todo listo para crear"}
        </h1>
        <p className="text-sm text-[#64748B]">
          {dictionary["review-summary"]?.[language] ?? "Revisa el resumen de lo que Fluxi generará para ti"}
        </p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
        <div className="flex items-center gap-3 px-4 py-3 bg-[#FAFAFF] rounded-2xl border border-[#F1F5F9]">
          <div className="w-10 h-10 rounded-xl bg-[#EDE9FE] flex items-center justify-center">
            <Layers className="w-5 h-5 text-[#6366F1]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{funnels.length}</p>
            <p className="text-xs text-gray-500">{dictionary["funnels-label"]?.[language] ?? "Embudos"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 bg-[#FAFAFF] rounded-2xl border border-[#F1F5F9]">
          <div className="w-10 h-10 rounded-xl bg-[#EDE9FE] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#6366F1]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
            </svg>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalSections}</p>
            <p className="text-xs text-gray-500">{dictionary["active-sections"]?.[language] ?? "Secciones"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 px-4 py-3 bg-[#FAFAFF] rounded-2xl border border-[#F1F5F9]">
          <div className="w-10 h-10 rounded-xl bg-[#EDE9FE] flex items-center justify-center">
            <Image className="w-5 h-5 text-[#6366F1]" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{totalAds}</p>
            <p className="text-xs text-gray-500">{dictionary["ads-count-label"]?.[language] ?? "Anuncios"}</p>
          </div>
        </div>
      </div>

      {/* Funnel breakdown */}
      <div className="max-w-lg mx-auto">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
          {dictionary["breakdown-by-funnel"]?.[language] ?? "DESGLOSE POR EMBUDO"}
        </p>
        <div className="space-y-3">
          {funnels.map((funnel, index) => {
            const enabledSections = funnel.sections.filter(s => s.enabled).length;
            const adCount = funnel.selectedAdTemplates?.length || 0;
            return (
              <div
                key={funnel.id}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[#F1F5F9] bg-white"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: funnelColors[index % funnelColors.length] }}
                >
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-gray-900">{funnel.name}</p>
                  </div>
                </div>
                <CheckCircle2 className="w-5 h-5 text-[#6366F1] flex-shrink-0" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
